import { useEffect, useRef, memo } from "react"

// Per-canvas callback counter stored in a WeakMap so each canvas
// instance tracks its own column count independently.
// Fixes Bug #3: the old module-level lastSecondMarker was shared across
// all AudioModule instances and used wall-clock time, causing the
// 1-second tick to appear at random x-positions.
const canvasCallbackCounts = new WeakMap()

/**
 * Normalizes input spectrogram object for consistent internal use.
 */
export const generateSpectrogramData = (spectrogram) => {
    if (!spectrogram) return { data: [], frameSize: 0 }
    
    // Support both raw data and wrapped objects
    return {
        data: spectrogram.data || spectrogram,
        frameSize: spectrogram.frameSize || 232 // Default to standard FFT size if missing
    }
}

/**
 * Renders a static 60x60 thumbnail of a full audio sample.
 * Wrapped in React.memo so it only repaints when the sample data changes —
 * prevents the entire grid from re-drawing on every recording tick (Bug #4).
 */
export const SpectrogramThumbnail = memo(({ data: sample }) => {
    const thumbRef = useRef(null)

    useEffect(() => {
        if (!thumbRef.current || !sample?.data) return

        const { data, frameSize } = sample
        const canvas = thumbRef.current
        const ctx = canvas.getContext("2d")
        const { width, height } = canvas

        const imgData = ctx.createImageData(width, height)
        const pixels = imgData.data

        // Calculate how many frames we have (data is a flat array of frames)
        const totalFrames = Math.floor(data.length / frameSize)

        for (let x = 0; x < width; x++) {
            // Map x-coordinate (0-59) to a frame index (0 to totalFrames-1)
            const frameIdx = Math.floor((x / width) * totalFrames)
            const frameOffset = frameIdx * frameSize

            for (let y = 0; y < height; y++) {
                // Map y-coordinate to frequency (bottom is low freq)
                const freqIdx = Math.floor(((height - 1 - y) / height) * frameSize)
                
                const value = data[frameOffset + freqIdx] || -80
                // Normalize -80dB...0dB to 0...1
                const norm = Math.max(0, Math.min(1, (value + 80) / 80))

                const r = Math.floor(norm * 200)
                const g = Math.floor(norm * 230)
                const b = Math.floor(90 + norm * 160)

                const pixelIdx = (y * width + x) * 4
                pixels[pixelIdx] = r
                pixels[pixelIdx + 1] = g
                pixels[pixelIdx + 2] = b
                pixels[pixelIdx + 3] = 255
            }
        }

        ctx.putImageData(imgData, 0, 0)
    }, [sample])

    return (
        <canvas
            ref={thumbRef}
            width={60}
            height={60}
            style={{ borderRadius: "4px", display: 'block' }}
        />
    )
})


export const drawSpectrogram = (data, frameSize, canvasRef) => {
    const canvas = canvasRef.current
    if (!canvas || !data) return

    const ctx = canvas.getContext("2d")
    const { width, height } = canvas

    const columnWidth = 10

    /* scroll canvas */
    ctx.drawImage(canvas, -columnWidth, 0)

    const imgData = ctx.createImageData(columnWidth, height)
    const pixels = imgData.data

    // Per-canvas callback counter — isolated from all other AudioModule instances
    const prevCount = canvasCallbackCounts.get(canvas) ?? 0
    const count = prevCount + 1
    canvasCallbackCounts.set(canvas, count)

    // TF.js recognizer fires ~4x/sec (overlapFactor 0.75 → 250 ms stride).
    // Drawing the marker every 4 columns places it at a consistent ~1-second boundary.
    const CALLBACKS_PER_SECOND = 4
    const drawSecondMarker = count % CALLBACKS_PER_SECOND === 0

    for (let y = 0; y < height; y++) {
        const freqPos = ((height - 1 - y) / height) * frameSize
        const low = freqPos | 0
        const high = Math.min(frameSize - 1, low + 1)
        const interp = freqPos - low

        const value =
            (data[low] || -80) * (1 - interp) +
            (data[high] || -80) * interp

        const norm = Math.max(0, Math.min(1, (value + 80) / 80))

        const r = norm * 200
        const g = norm * 230
        const b = 90 + norm * 160

        for (let x = 0; x < columnWidth; x++) {
            const i = (y * columnWidth + x) * 4
            pixels[i] = r
            pixels[i + 1] = g
            pixels[i + 2] = b
            pixels[i + 3] = 255
        }
    }

    ctx.putImageData(imgData, width - columnWidth, 0)

    /* draw a 1px second marker */
    if (drawSecondMarker) {
        ctx.fillStyle = "rgba(255,255,255,0.9)"
        ctx.fillRect(width - 1, 0, 1, height)
    }
}
